import { lazy, StrictMode, Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import './index.css';
import { prefersReducedMotion, resolveTheme, useSettingsStore } from './store/settingsStore.js';
import { HomeScreen } from './screens/HomeScreen.js';
import { BrowseScreen } from './screens/BrowseScreen.js';
import { HowToPlayScreen } from './screens/HowToPlayScreen.js';
import { ProfileScreen } from './screens/ProfileScreen.js';
import { GameRecordScreen } from './screens/GameRecordScreen.js';
import { ModelProfileScreen } from './screens/ModelProfileScreen.js';
import { ModelsScreen } from './screens/ModelsScreen.js';
import { AdminScreen } from './screens/AdminScreen.js';
import { NotFoundScreen } from './screens/NotFoundScreen.js';
import { RoomPage } from './screens/room/RoomPage.js';
import { Toasts } from './components/overlays/Toasts.js';
import { MaintenanceNotice } from './components/overlays/MaintenanceNotice.js';
import { AccountProvider } from './components/account/AccountProvider.js';
import { RoomConnectionProvider } from './hooks/roomConnection.js';

/**
 * The component gallery. Unlinked from every navigation and reachable only by
 * typing the path, which is all it needs to be: nothing on it is sensitive and
 * nothing on it talks to a server.
 *
 * Lazy on purpose. It imports every screen and every fixture, and a player who
 * never types /dev must not download any of it.
 */
const DevGallery = lazy(() =>
  import('./screens/dev/DevGallery.js').then((module) => ({ default: module.DevGallery })),
);

/**
 * Theme and reduced motion are stamped onto the root element rather than passed
 * through context, because the tokens and the animation opt-outs in index.css
 * are CSS-level and have to reach elements React does not own — the scrim, the
 * body background, and anything portalled.
 */
function ThemeSync({ children }: { children: React.ReactNode }) {
  const theme = useSettingsStore((s) => s.theme);
  const reducedMotion = useSettingsStore((s) => s.reducedMotion);

  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const resolved = resolveTheme(theme);
      root.dataset.theme = resolved;
      // Kept in step with the pre-paint script in index.html, which sets the
      // same two things before React exists. This half handles the toggle and
      // the OS changing under a 'system' preference.
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', resolved === 'light' ? '#f4f7fb' : '#040b13');
      // Whatever the player settled on. `prefersReducedMotion` reads the OS on a
      // device that has never answered, writes that answer down, and never asks
      // again — so this is the stored preference from the second call onwards.
      root.dataset.reducedMotion = String(prefersReducedMotion());
    };

    apply();

    // 'system' has to follow the OS while the tab is open, not only at load —
    // but that is the COLOUR scheme only. There is deliberately no listener on
    // `prefers-reduced-motion` any more: the machine is consulted once to seed
    // the setting, and after that the setting is the answer. A listener here
    // would walk in and overwrite a choice the player made in the app, live,
    // and it is why turning motion back on used to appear to do nothing.
    const colorScheme = window.matchMedia('(prefers-color-scheme: light)');
    colorScheme.addEventListener('change', apply);

    return () => {
      colorScheme.removeEventListener('change', apply);
    };
  }, [theme, reducedMotion]);

  return children;
}

// The root is cached on the container rather than created on every evaluation.
//
// Vite re-executes this module on a hot update, and a second `createRoot` on a
// container that already has one mounts a SECOND React tree over the first.
// Both stay live, so in development two sockets join, two Clerk instances load,
// and a bug that only exists because of that is indistinguishable from a real
// one. Production evaluates this once and never notices.
const container = document.getElementById('root')! as HTMLElement & {
  __root?: ReturnType<typeof createRoot>;
};
(container.__root ??= createRoot(container)).render(
  <StrictMode>
    <ThemeSync>
      <BrowserRouter>
        {/* Inside the router because Clerk's own navigations are handed to it,
            and because a sign-in that reloaded the page would drop a player out
            of the room they were sitting in. Mounts Clerk only when /api/config
            comes back with a key; with none, every screen below sees a guest. */}
        <AccountProvider>
          {/* The socket lives above the routes, not inside the room page.
              Joining by code is started on the home screen and finished there:
              the connection outlives the navigation, so there is no page in
              between announcing a wait the player was already watching. Inside
              the router because it drops the connection when the URL leaves the
              room, and inside AccountProvider because a join carries a session
              token. See hooks/roomConnection.tsx. */}
          <RoomConnectionProvider>
          <Routes>
            <Route path="/" element={<HomeScreen />} />
            <Route path="/browse" element={<BrowseScreen />} />
            {/* No /solo route. Playing alone is a button on the home screen
                that creates an ordinary private room and asks the room page to
                seat one bot, so there is no second screen to route to. */}
            <Route path="/how-to-play" element={<HowToPlayScreen />} />
            <Route path="/room/:code" element={<RoomPage />} />
            <Route path="/u/:username" element={<ProfileScreen />} />
            {/* Every finished game is readable by anyone with the link. The id
                is the server's own UUID, so there is nothing to enumerate. */}
            <Route path="/game/:gameId" element={<GameRecordScreen />} />
            {/* An AI opponent is a player and gets a player's page. Two path
                segments because an OpenRouter id is `provider/name` and a `%2F`
                inside a path is mangled by enough proxies to be worth avoiding;
                `modelPath` in `lib/format.ts` is the one place that is built. */}
            <Route path="/models" element={<ModelsScreen />} />
            <Route path="/m/:provider/:name" element={<ModelProfileScreen />} />
            <Route path="/admin" element={<AdminScreen />} />
            <Route
              path="/dev"
              element={
                <Suspense fallback={null}>
                  <DevGallery />
                </Suspense>
              }
            />
            <Route path="*" element={<NotFoundScreen />} />
          </Routes>
          </RoomConnectionProvider>
          {/* One toast surface for the whole app, outside the routes.
              Everything that floats lands here: the room's own lines, and the
              restart notice. The restart is outside the routes for its own
              reason — the announcement usually arrives seconds before the
              socket drops, and a player who backs out to the home screen in
              between should still be told why the server is about to go away. */}
          <Toasts />
          <MaintenanceNotice />
        </AccountProvider>
      </BrowserRouter>
    </ThemeSync>
  </StrictMode>,
);
